import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { FlaskConical, Loader2, AlertTriangle, X, RefreshCw } from 'lucide-react';

const RETRYABLE_ALERTS = ['driver-underway', 'driver-arrived', 'in-progress', 'complete', 'no-show-action', 'no-show-photos'];
import { Button } from '../ui/button';
import { toast } from 'sonner';

const formatLocalTime = (localTime) => {
  if (!localTime) return '—';
  const m = String(localTime).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}, ${m[4]}:${m[5]}`;
  return localTime;
};

const statusStyle = (status) => {
  switch (status) {
    case 'completed': return 'bg-emerald-100 text-emerald-700';
    case 'free_cancelled':
    case 'paid_cancelled':
    case 'cancelled': return 'bg-red-100 text-red-700';
    case 'in_progress': return 'bg-sky-100 text-sky-700';
    default: return 'bg-slate-100 text-slate-600';
  }
};

export default function TransferzTestRides() {
  const { token } = useAuth();
  const [rides, setRides] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchAll = useCallback(() => {
    return Promise.all([
      axios.get(`${API}/transferz/test-rides`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setRides(r.data)).catch(() => toast.error('Impossibile caricare le corse di collaudo')),
      axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setDrivers(r.data.filter(u => ['driver', 'admin-driver'].includes(u.role) && u.is_active !== false)))
        .catch(() => {}),
    ]);
  }, [token]);

  useEffect(() => {
    fetchAll().finally(() => setLoading(false));
  }, [fetchAll]);

  const confirmCancel = async (ride) => {
    setBusy(ride.id);
    try {
      const r = await axios.post(`${API}/rides/${ride.id}/transferz-cancel-confirm`, {}, authHeaders);
      toast.success(`Corsa impostata su ${r.data.status}`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setBusy(null);
    }
  };

  const dismissAlert = async (ride, type) => {
    try {
      await axios.post(`${API}/rides/${ride.id}/transferz-alert-dismiss`, { type }, authHeaders);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const onSelectDriver = (ride, driverId) => {
    if (!driverId) {
      toast.error("Su una corsa Transferz l'autista può solo essere sostituito, non rimosso");
      return;
    }
    const driver = drivers.find(d => d.id === driverId);
    if (!driver) return;
    setConfirmData({ ride, driver });
  };

  const retryAlert = async (ride) => {
    setBusy(ride.id);
    try {
      await axios.post(`${API}/rides/${ride.id}/transferz-status-retry`, {}, authHeaders);
      toast.success('Nuovo tentativo avviato');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel riprovare');
    } finally {
      setBusy(null);
    }
  };

  const confirmAssign = async () => {
    if (!confirmData || confirming) return;
    setConfirming(true);
    try {
      await axios.post(`${API}/rides/${confirmData.ride.id}/transferz-assign-driver`, { driver_id: confirmData.driver.id }, authHeaders);
      toast.success('Assegnato con successo, anche su Transferz');
      setConfirmData(null);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore assegnazione Transferz');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="p-4 lg:p-8" data-testid="transferz-test-rides-panel">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-10 w-10 rounded-lg bg-violet-100 flex items-center justify-center">
          <FlaskConical className="h-5 w-5 text-violet-600" />
        </div>
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Corse Transferz (collaudo)</h1>
          <p className="text-sm text-slate-500">Corse create automaticamente dall&apos;ambiente di staging Transferz — solo admin</p>
        </div>
      </div>
      <div className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-50 border border-violet-200 text-violet-700 text-xs font-semibold">
        <FlaskConical className="h-3.5 w-3.5" /> Dati di TEST — non compaiono nelle viste operative
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento...
        </div>
      ) : rides.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500" data-testid="test-rides-empty">
          Nessuna corsa di collaudo creata finora.
        </div>
      ) : (
        <div className="space-y-4">
          {rides.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-xl shadow-sm p-4" data-testid={`test-ride-${r.transferz_journey_id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-bold bg-violet-600 text-white" data-testid={`test-badge-${r.transferz_journey_id}`}>TEST</span>
                <span className="font-semibold text-slate-900">{r.customer_name}</span>
                <span className="text-xs font-mono text-slate-500">{r.booking_number} · journey {r.transferz_journey_id}</span>
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${statusStyle(r.status)}`}>{r.status}</span>
              </div>

              {r.transferz_cancel_alert && (
                <div className="mb-2 bg-red-50 border border-red-300 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`cancel-alert-${r.transferz_journey_id}`}>
                  <div className="flex items-center gap-2 text-sm text-red-700 font-medium">
                    <AlertTriangle className="h-4 w-4" />
                    Transferz ha cancellato questo viaggio ({r.transferz_cancel_alert.transferz_status})
                  </div>
                  <Button size="sm" onClick={() => confirmCancel(r)} disabled={busy === r.id}
                    className="bg-red-600 hover:bg-red-700 text-white h-8"
                    data-testid={`confirm-cancel-${r.transferz_journey_id}`}>
                    Imposta su {r.transferz_cancel_alert.suggested_status === 'free_cancelled' ? 'cancellata gratuita' : 'cancellata con addebito'}
                  </Button>
                </div>
              )}

              {r.transferz_unassigned_alert && (
                <div className="mb-2 bg-red-50 border border-red-300 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`unassigned-alert-${r.transferz_journey_id}`}>
                  <div className="flex items-center gap-2 text-sm text-red-700 font-medium">
                    <AlertTriangle className="h-4 w-4" />
                    Transferz ha tolto questo viaggio dalla nostra flotta — decidi tu cosa fare
                  </div>
                  <Button size="sm" variant="outline" onClick={() => dismissAlert(r, 'unassigned')} className="h-8 border-red-300 text-red-600" data-testid={`dismiss-unassigned-${r.transferz_journey_id}`}>
                    <X className="h-4 w-4 mr-1" /> Ho visto
                  </Button>
                </div>
              )}

              {r.transferz_status_alert && (
                <div className="mb-2 bg-orange-50 border border-orange-300 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`status-alert-${r.transferz_journey_id}`}>
                  <div className="flex items-start gap-2 text-sm text-orange-800 font-medium">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      {r.transferz_status_alert.message}
                      {r.transferz_status_alert.detail && <span className="block text-xs font-normal">{r.transferz_status_alert.detail}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {RETRYABLE_ALERTS.includes(r.transferz_status_alert.status) && (
                      <Button size="sm" onClick={() => retryAlert(r)} disabled={busy === r.id} className="h-8 bg-orange-600 hover:bg-orange-700 text-white" data-testid={`retry-status-${r.transferz_journey_id}`}>
                        <RefreshCw className="h-4 w-4 mr-1" /> Riprova
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => dismissAlert(r, 'status')} className="h-8 border-orange-300 text-orange-700" data-testid={`dismiss-status-${r.transferz_journey_id}`}>
                      <X className="h-4 w-4 mr-1" /> Ho visto
                    </Button>
                  </div>
                </div>
              )}

              {r.transferz_updated_alert && (
                <div className="mb-2 bg-amber-50 border border-amber-300 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`updated-alert-${r.transferz_journey_id}`}>
                  <div className="flex items-center gap-2 text-sm text-amber-800 font-medium">
                    <AlertTriangle className="h-4 w-4" />
                    Dati aggiornati da Transferz dopo l&apos;assegnazione — ricontrolla la corsa
                  </div>
                  <Button size="sm" variant="outline" onClick={() => dismissAlert(r, 'updated')} className="h-8 border-amber-300 text-amber-700" data-testid={`dismiss-updated-${r.transferz_journey_id}`}>
                    <X className="h-4 w-4 mr-1" /> Ho visto
                  </Button>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-1 text-sm text-slate-600">
                <div><span className="text-slate-400">Ritiro:</span> {r.pickup_location || '—'}</div>
                <div><span className="text-slate-400">Destinazione:</span> {r.dropoff_location || '—'}</div>
                <div><span className="text-slate-400">Data ritiro:</span> {formatLocalTime(r.date_time)}</div>
                <div><span className="text-slate-400">Veicolo:</span> {r.vehicle_type || '—'} · <span className="text-slate-400">Prezzo:</span> {r.price != null ? `€${Number(r.price).toFixed(2)}` : '—'} · <span className="text-slate-400">Volo:</span> {r.flight_number || '—'}</div>
              </div>

              <div className="mt-1 text-xs font-mono text-slate-400" data-testid={`coords-${r.transferz_journey_id}`}>
                Pickup: {r.pickup_lat ?? 'mancante'}, {r.pickup_lng ?? 'mancante'} · Dropoff: {r.dropoff_lat ?? 'mancante'}, {r.dropoff_lng ?? 'mancante'}
              </div>

              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-slate-500">Autista:</span>
                <select
                  value={r.assigned_driver_id || ''}
                  onChange={(e) => onSelectDriver(r, e.target.value)}
                  disabled={busy === r.id}
                  className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white"
                  data-testid={`assign-driver-${r.transferz_journey_id}`}
                >
                  <option value="">— Nessun autista —</option>
                  {drivers.map(d => (
                    <option key={d.id} value={d.id}>{d.name}{!d.phone ? ' (telefono mancante)' : ''}</option>
                  ))}
                </select>
                {r.assigned_driver_name && <span className="text-xs text-slate-500">attuale: {r.assigned_driver_name}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmData && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="assign-confirm-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
            <h2 className="text-lg font-bold text-slate-900 mb-1">Conferma assegnazione su Transferz</h2>
            <p className="text-sm text-slate-500 mb-4">L&apos;autista verrà comunicato a Transferz (sostituisce l&apos;eventuale autista precedente).</p>
            <div className="space-y-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
              <div><span className="text-slate-400">Autista:</span> <span className="font-semibold text-slate-900" data-testid="confirm-driver-name">{confirmData.driver.name}</span></div>
              <div><span className="text-slate-400">Telefono:</span> <span className="font-mono" data-testid="confirm-driver-phone">{confirmData.driver.phone || '—'}</span></div>
              <div><span className="text-slate-400">Viaggio Transferz:</span> <span className="font-mono">{confirmData.ride.booking_number || '—'} · journey {confirmData.ride.transferz_journey_id}</span></div>
              <div><span className="text-slate-400">Ritiro:</span> {confirmData.ride.pickup_location || '—'}</div>
              <div><span className="text-slate-400">Destinazione:</span> {confirmData.ride.dropoff_location || '—'}</div>
              <div><span className="text-slate-400">Data e ora:</span> {formatLocalTime(confirmData.ride.date_time)}</div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmData(null)} disabled={confirming} data-testid="assign-cancel-btn">
                Annulla
              </Button>
              <Button onClick={confirmAssign} disabled={confirming} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="assign-confirm-btn">
                {confirming ? (<><Loader2 className="h-4 w-4 animate-spin mr-2" /> Invio a Transferz...</>) : 'Conferma assegnazione'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
